/**
 * animateCount — pure rAF tween from `from` to `to` over `duration` ms,
 * ease-out cubic. Calls `onUpdate(currentValue)` each frame and returns a
 * cleanup function that cancels the in-flight rAF.
 *
 * No deps. SSR-safe (no-op if window/requestAnimationFrame missing).
 */

export function animateCount(
  from: number,
  to: number,
  duration: number,
  onUpdate: (v: number) => void,
): () => void {
  if (
    typeof window === 'undefined' ||
    typeof window.requestAnimationFrame !== 'function'
  ) {
    onUpdate(to);
    return () => {};
  }

  if (duration <= 0 || from === to) {
    onUpdate(to);
    return () => {};
  }

  let rafId = 0;
  let cancelled = false;
  const start = performance.now();
  const delta = to - from;

  const tick = (now: number) => {
    if (cancelled) return;
    const elapsed = now - start;
    const t = Math.min(1, Math.max(0, elapsed / duration));
    // ease-out cubic
    const eased = 1 - Math.pow(1 - t, 3);
    const value = from + delta * eased;
    onUpdate(value);
    if (t < 1) {
      rafId = window.requestAnimationFrame(tick);
    } else {
      onUpdate(to);
    }
  };

  rafId = window.requestAnimationFrame(tick);

  return () => {
    cancelled = true;
    if (rafId) window.cancelAnimationFrame(rafId);
  };
}
